/* eslint-disable max-lines */
import {
  getContributorAvatar,
  getContributorAvatarUrl
} from 'common/constants/Avatars';
import {
  SHORT_DATE_FORMAT,
  SEARCH_DATE_FORMAT,
  PM_PROVIDERS,
  TIMELINE_TYPES as timelineTypes,
  DAYS_PARAM
} from 'common/constants/constants';
import _ from 'lodash';
import moment from 'moment';
import clubhouseFeature from 'images/icons/CH-feature.svg';
import clubhouseBug from 'images/icons/CH-bug.svg';
import clubhouseChore from 'images/icons/CH-Chore.svg';
import { CIRCLE_TYPES, getCircleObject, ISSUE_STATES } from './pulse.common';
import { extractAlertsFromBranch } from 'common/services/graphql.service';

const emptyIterationTimelineData = {
  iterations: [
    {
      isCurrent: true,
      iterationName: '',
      dates: Array.from(Array(15), (_, i) =>
        moment()
          .startOf('day')
          .add(1, 'day')
          .subtract(14 - i, DAYS_PARAM)
      )
    }
  ]
};

const mapPmProviderToIssueTypesIcons = {
  [PM_PROVIDERS.CLUBHOUSE]: {
    feature: clubhouseFeature,
    bug: clubhouseBug,
    chore: clubhouseChore
  }
};

export const getIssueTypeIconByPmProvider = (pmProvider, issueType) =>
  mapPmProviderToIssueTypesIcons[pmProvider]?.[_.lowerCase(issueType)] || null;

const contributorsToAvatars = (
  contributorIds,
  allContributors,
  selectedContributorsIds = []
) => {
  const contributorsById = _.keyBy(allContributors, 'id');
  return contributorIds.map(contributorId => {
    const { name, extended_data = {} } = contributorsById[contributorId] || {};
    return {
      id: contributorId,
      name: name || extended_data.username,
      url: getContributorAvatarUrl({ id: contributorId, extended_data }),
      isGrayedOut:
        !_.isEmpty(selectedContributorsIds) &&
        !selectedContributorsIds.includes(contributorId)
    };
  });
};

const generateDatesToArrayMap = (length, from) =>
  Array.from(Array(length), (_, i) =>
    moment(from)
      .startOf('day')
      .subtract(i, DAYS_PARAM)
      .format(SEARCH_DATE_FORMAT)
  ).reduce((acc, cur) => ({ [cur]: [], ...acc }), {});

export const getBranchItems = (timeLineByType, selectedContributorIds) => {
  const merges = timeLineByType[timelineTypes.MERGE]
    ? timeLineByType[timelineTypes.MERGE].map(activity => ({
        contributor_id: activity.contributor_id,
        timestamp: activity.timestamp,
        title: `merged with ${activity.code_changes} code changes`,
        avatars: !activity.contributor_id
          ? [
              {
                id: null,
                isGrayedOut: false,
                name: '',
                url: getContributorAvatarUrl({ id: null, extended_data: {} })
              }
            ]
          : [
              getContributorAvatar({
                id: activity.contributor_id,
                selectedContributorIds
              })
            ],
        circles: getCircles([activity], selectedContributorIds)
      }))
    : [];
  const releases = timeLineByType[timelineTypes.RELEASE]
    ? timeLineByType[timelineTypes.RELEASE].map(activity => ({
        contributor_id: activity.contributor_id,
        timestamp: activity.timestamp,
        title: 'released',
        avatars: [
          getContributorAvatar({
            id: activity.contributor_id,
            selectedContributorIds
          })
        ],
        circles: getCircles([activity], selectedContributorIds),
        highlightedTimestamp: true
      }))
    : [];
  const commits = timeLineByType[timelineTypes.ACTIVITY]
    ? timeLineByType[timelineTypes.ACTIVITY].map(activity => ({
        contributor_id: activity.contributor_id,
        timestamp: activity.timestamp,
        title: `${activity.commit_count} commit${
          activity.commit_count > 1 ? 's' : ''
        }`,
        avatars: [
          getContributorAvatar({
            id: activity.contributor_id,
            selectedContributorIds
          })
        ],
        circles: getCircles([activity], selectedContributorIds)
      }))
    : [];
  const prs = timeLineByType[timelineTypes.PR]
    ? timeLineByType[timelineTypes.PR].map(activity => ({
        contributor_id: activity.contributor_id,
        timestamp: activity.timestamp,
        title: 'pull request opened',
        pullRequest: activity.pullRequest,
        avatars: [
          getContributorAvatar({
            id: activity.contributor_id,
            selectedContributorIds
          })
        ],
        circles: getCircles([activity], selectedContributorIds)
      }))
    : [];

  return _.orderBy(
    [...commits, ...merges, ...prs, ...releases],
    ['timestamp'],
    ['desc']
  );
};

const generateTooltipSubtitleItems = timeLineByType => {
  const createSubtitleItem = (text, timelineType) => {
    const { timestamp } = _.maxBy(timeLineByType[timelineType], 'timestamp');
    return {
      text,
      timestamp,
      highlightedTimestamp: timelineType === timelineTypes.RELEASE
    };
  };

  const countTypes = timelineType =>
    _.get(timeLineByType, timelineType, []).length;
  const merges = countTypes(timelineTypes.MERGE);
  const releases = countTypes(timelineTypes.RELEASE);
  const subtitleItems = [];
  if (countTypes(timelineTypes.ACTIVITY)) {
    const commits = _.sumBy(
      timeLineByType[timelineTypes.ACTIVITY],
      'commit_count'
    );
    const text = `${commits} commit${commits > 1 ? 's' : ''}`;
    subtitleItems.push(createSubtitleItem(text, timelineTypes.ACTIVITY));
  }
  if (countTypes(timelineTypes.PR)) {
    subtitleItems.push(
      createSubtitleItem('pull request opened', timelineTypes.PR)
    );
  }
  if (merges) {
    const codeChanges = _.sumBy(
      timeLineByType[timelineTypes.MERGE],
      'code_changes'
    );
    const text = `${merges > 1 ? `${merges} ` : ''}merge${
      merges > 1 ? 's' : 'd'
    } with ${codeChanges} code changes`;
    subtitleItems.push(createSubtitleItem(text, timelineTypes.MERGE));
  }
  if (releases) {
    const text = `${releases > 1 ? `${releases} ` : ''}release${
      releases > 1 ? 's' : 'd'
    }`;
    subtitleItems.push(createSubtitleItem(text, timelineTypes.RELEASE));
  }
  return _.orderBy(subtitleItems, ['timestamp'], ['desc']);
};

const getTooltipData = (
  date,
  timelineData,
  contributors,
  selectedContributorsIds
) => {
  const timeLineByBranch = _.groupBy(timelineData, 'branchName');
  const items = Object.entries(timeLineByBranch).map(
    ([branchName, branchTimeline]) => {
      const circles = getCircles(branchTimeline, selectedContributorsIds);
      const contributorIds = branchTimeline.reduce((acc, cur) => {
        acc.add(cur.contributor_id);
        return acc;
      }, new Set());
      const { timestamp } = _.maxBy(branchTimeline, 'timestamp') || {};
      const subtitleItems = generateTooltipSubtitleItems(
        _.groupBy(branchTimeline, 'type')
      );
      return {
        title: branchName,
        pullRequestUrl: _.get(branchTimeline, '[0].pullRequest.html_url'),
        avatars: contributorsToAvatars(
          Array.from(contributorIds),
          contributors,
          selectedContributorsIds
        ),
        circles,
        subtitleItems,
        timestamp,
        timeline: branchTimeline
      };
    }
  );
  return {
    title: moment(date).format(SHORT_DATE_FORMAT),
    isToday: moment(date).startOf('day').isSame(moment().startOf('day')),
    items
  };
};

const isCircleContainsActivityFromSelectedContributors = (
  timelineData,
  selectedContributorsIds
) =>
  selectedContributorsIds.length === 0 ||
  _.intersection(
    timelineData.map(({ contributor_id }) => contributor_id),
    selectedContributorsIds
  ).length > 0;

const getCircles = (timelineData, selectedContributorsIds = []) => {
  const timelineByType = _.groupBy(timelineData, 'type');
  const activeWorkCircleData = timelineByType[timelineTypes.ACTIVITY] || [];
  const prOpenCircleData = timelineByType[timelineTypes.PR] || [];
  const doneCircleData = _.flatten([
    timelineByType[timelineTypes.MERGE] || [],
    timelineByType[timelineTypes.RELEASE] || []
  ]);

  const circles = [];
  if (activeWorkCircleData.length || prOpenCircleData.length) {
    const isGrayedOut = !isCircleContainsActivityFromSelectedContributors(
      [...activeWorkCircleData, ...prOpenCircleData],
      selectedContributorsIds
    );
    let commitCount = _.sumBy(activeWorkCircleData, 'commit_count');
    commitCount += prOpenCircleData.length;
    circles.push(
      getCircleObject(CIRCLE_TYPES.ACTIVE, commitCount, isGrayedOut)
    );
  }
  if (doneCircleData.length) {
    const isGrayedOut = !isCircleContainsActivityFromSelectedContributors(
      doneCircleData,
      selectedContributorsIds
    );
    const doneByBranch = _.groupBy(doneCircleData, 'branchName');
    const releases = doneCircleData.filter(
      ({ type }) => type === timelineTypes.RELEASE
    );
    const codeChanges = _.sumBy(
      Object.values(doneByBranch),
      '[0].code_changes'
    );
    circles.push(
      getCircleObject(
        releases.length ? CIRCLE_TYPES.DEPLOY : CIRCLE_TYPES.MERGE,
        codeChanges,
        isGrayedOut
      )
    );
  }
  return circles;
};

export const getGraphItemsData = (
  branches,
  contributors,
  selectedContributorsIds = []
) => {
  if (branches.length === 0) {
    return [];
  }
  const datesMap = generateDatesToArrayMap(14);
  branches
    .flatMap(({ timeline, name, repository_name, pull_request }) =>
      timeline.map(timelineItem => ({
        branchName: name.replace('origin/', `${repository_name}/`),
        pullRequest: pull_request,
        ...timelineItem
      }))
    )
    .forEach(
      timelineItem =>
        datesMap[timelineItem.date] &&
        datesMap[timelineItem.date].push(timelineItem)
    );
  const graphItems = Object.entries(datesMap).map(([date, timelineData]) => {
    const circles = getCircles(timelineData, selectedContributorsIds);
    return circles.length
      ? {
          circles,
          tooltipData: getTooltipData(
            date,
            timelineData,
            contributors,
            selectedContributorsIds
          )
        }
      : {};
  });
  graphItems.push({}); // for tomorrow item
  return graphItems;
};

export const getTopBarData = (
  tableData,
  issueTypeFilters = [],
  sensor_id = PM_PROVIDERS.JIRA
) => {
  const filteredTableData = tableData.filter(
    ({ issueType }) =>
      !issueTypeFilters.length || issueTypeFilters.includes(issueType)
  );
  const issuesByState = _.groupBy(filteredTableData, 'jiraIssueState');
  const issuesByType = _.groupBy(tableData, 'issueType');

  return {
    issueTypes: Object.entries(issuesByType).map(([type, issues]) => ({
      type,
      icon:
        _.get(issues, '[0].iconUrl') ||
        getIssueTypeIconByPmProvider(sensor_id, type)
    })),
    wip: (issuesByState[ISSUE_STATES.IN_PROGRESS] || []).length,
    toDo: (issuesByState[ISSUE_STATES.INITIATED] || []).length,
    done: (issuesByState[ISSUE_STATES.DONE] || []).length
  };
};

const getReposInfo = reposCount => {
  if (!reposCount) {
    return null;
  }
  return reposCount === 1 ? 'in one repo' : `across ${reposCount} repos`;
};

export const mapDataToUiTableData = (
  data,
  contributors,
  selectedContributorsIds = []
) => {
  const { issues } = data;
  if (!issues) {
    return [];
  }
  const timeLineFirstDateMoment = moment().startOf('day').subtract(13, 'day');
  return issues.map(issue => {
    const {
      id,
      branches,
      title,
      provider_state,
      issue_state,
      subtasks = [],
      provider_type_icon_url,
      html_url,
      provider_type,
      provider_key,
      properties,
      shadow_work,
      provider_attributes
    } = issue;
    const assignees = _.get(provider_attributes, 'assignees');
    const branchesWithSubTaskBranches = (branches || []).concat(
      (subtasks || []).flatMap(({ branches }) => branches)
    );
    const contributorsSet =
      branchesWithSubTaskBranches.reduce((acc, branch) => {
        branch.contributor_ids.forEach(id => acc.add(id));
        return acc;
      }, new Set()) || [];

    const totalCodeChanges = _.sumBy(
      branchesWithSubTaskBranches,
      'pull_request.stats["pr.size.total"]'
    );
    const reposSet = new Set(
      branchesWithSubTaskBranches.map(branch =>
        _.get(branch, 'repository_name', null)
      )
    );
    reposSet.delete(null);
    const branchesByState = _.groupBy(branchesWithSubTaskBranches, 'state');

    const activeCount = (branchesByState['active'] || []).length;
    const deployedCount = (branchesByState['deployed'] || []).length;
    const mergedCount = (branchesByState['merged'] || []).length;
    const firstActivity = branchesWithSubTaskBranches
      .map(({ first_committed_at }) =>
        moment(first_committed_at).startOf('day')
      )
      .reduce(
        (acc, cur) => (acc.isBefore(cur) ? acc : cur),
        moment().startOf('day')
      );
    const carryOver = timeLineFirstDateMoment.isAfter(firstActivity)
      ? `work started ${firstActivity.format(SHORT_DATE_FORMAT)}`
      : null;
    const latestActivity =
      branchesWithSubTaskBranches
        .flatMap(({ timeline }) => timeline)
        .map(timeline => timeline.timestamp || timeline.date)
        .sort()
        .pop() || '';

    const alerts = branchesWithSubTaskBranches.flatMap(branch => {
      return extractAlertsFromBranch(branch, contributors);
    });

    return {
      jiraIssueState: issue_state,
      iconUrl: provider_type_icon_url,
      jiraLink: html_url,
      issueId: id,
      issueName: title,
      issueType: provider_type,
      issueState: provider_state,
      issueKey: provider_key,
      storyPoints: _.get(properties, 'story_points', null),
      contributors: Array.from(contributorsSet),
      avatars: contributorsToAvatars(
        Array.from(contributorsSet),
        contributors,
        selectedContributorsIds
      ),
      totalCodeChanges,
      reposInfo: getReposInfo(reposSet.size),
      activeCount,
      deployedCount,
      mergedCount,
      carryOver,
      graphItems: getGraphItemsData(
        branchesWithSubTaskBranches,
        contributors,
        selectedContributorsIds
      ),
      alerts,
      latestActivity,
      shadow_work,
      assignees
    };
  });
};

export const mapDataToUiPeopleFilterData = (data, selectedTeamContributors) => {
  const { issues } = data;
  if (!issues) {
    return [];
  }
  const getAllIssuesBranches = ({ branches, subtasks }) =>
    (branches || []).concat(
      (subtasks || []).flatMap(({ branches }) => branches)
    );
  const flatUniqBranches = _.chain(issues)
    .flatMap(getAllIssuesBranches)
    .uniqBy(({ id: branchId }) => branchId)
    .value();

  const issuesContributors = issues.map(issue => ({
    ...issue,
    contributorIds: _.chain(getAllIssuesBranches(issue))
      .flatMap(({ contributor_ids }) => contributor_ids)
      .uniq()
      .value()
  }));

  const issuesByState = _.groupBy(issuesContributors, 'issue_state');

  const getBranchesPerContrib = id =>
    flatUniqBranches.filter(({ contributor_ids = [] }) =>
      contributor_ids.includes(id)
    ).length;

  const getStatePerContrib = (id, issueState) =>
    _.get(issuesByState, `[${issueState}]`, []).filter(
      ({ contributorIds = [] }) => contributorIds.includes(id)
    ).length;
  return selectedTeamContributors.map(({ id, name, extended_data }) => ({
    id,
    name,
    'in-progress': getStatePerContrib(id, ISSUE_STATES.IN_PROGRESS),
    done: getStatePerContrib(id, ISSUE_STATES.DONE),
    created: getStatePerContrib(id, ISSUE_STATES.INITIATED),
    branches: getBranchesPerContrib(id),
    avatar: getContributorAvatarUrl({ id, extended_data })
  }));
};

const resolveIterations = iterations => {
  const lastEndDate = iterations[0].end_date;
  let resolvedIterations = [];
  // fixing current date is after last iteration end_date:
  if (moment(lastEndDate).startOf('day').isBefore(moment().startOf('day'))) {
    const dummyIteration = {
      name: '',
      start_date: lastEndDate,
      end_date: moment().add(2, 'day').format(SEARCH_DATE_FORMAT)
    };
    resolvedIterations.push(dummyIteration);
  }
  resolvedIterations = resolvedIterations.concat(iterations);
  let lastStartDate = resolvedIterations[0].start_date;

  // fixing iterations have gaps between dates:
  return resolvedIterations.map((iteration, i) => {
    if (i === 0) {
      return iteration;
    }
    const endDateDiff = moment(lastStartDate)
      .startOf('day')
      .diff(moment(iteration.end_date).startOf('day'), 'day');
    const resolvedEndDate = moment(iteration.end_date)
      .add(endDateDiff, 'day')
      .startOf('day')
      .format(SEARCH_DATE_FORMAT);
    lastStartDate = iteration.start_date;
    return { ...iteration, end_date: resolvedEndDate };
  });
};

export const getTimelineData = iterations => {
  try {
    const timelineData = { iterations: [] };
    let timelineIteration = {};
    if (!iterations.length) {
      return timelineIteration;
    }
    const resolvedIterations = resolveIterations(iterations);
    const now = moment().startOf('day');
    let curIterationIndex = 0;
    const currentTimelineDate = moment().startOf('day').subtract(13, 'day');
    while (currentTimelineDate.isSameOrBefore(now) && curIterationIndex >= 0) {
      const curIteration = resolvedIterations[curIterationIndex];
      if (!curIteration) {
        throw new Error(
          JSON.stringify({ curIterationIndex, resolvedIterations })
        );
      }
      const startDate = moment(curIteration.start_date).startOf('day');
      // we subtract because in our system first_date of iteration === last_date of previous iteration. we don't need to subtract for current iteration;
      const endDate = moment(curIteration.end_date)
        .subtract(curIterationIndex === 0 ? 0 : 1, 'day')
        .startOf('day');
      if (
        currentTimelineDate.isBetween(startDate, endDate) ||
        currentTimelineDate.isSame(endDate) ||
        currentTimelineDate.isSame(startDate)
      ) {
        if (timelineIteration.iterationName === undefined) {
          timelineIteration.iterationName = curIteration.name;
          timelineIteration.dates = [];
          timelineIteration.isCurrent = curIterationIndex === 0;
        }
        timelineIteration.dates.push(currentTimelineDate.toDate());
        if (currentTimelineDate.isSame(endDate)) {
          timelineData.iterations.push(timelineIteration);
          timelineIteration = {};
          curIterationIndex--;
        }
        currentTimelineDate.add(1, 'day');
      } else {
        curIterationIndex++;
      }
    }

    const curIteration = resolvedIterations[0];
    if (now.isSame(moment(curIteration.end_date).startOf('day'))) {
      timelineData.isIterationLastDay = curIteration.name;
      timelineIteration = {
        iterationName: '',
        dates: [now.add(1, 'day').toDate()]
      };
    } else {
      if (now.isSame(moment(curIteration.start_date).startOf('day'))) {
        timelineData.isIterationFirstDay = curIteration.name;
      }
      timelineIteration.dates.push(now.add(1, 'day').toDate());
    }
    timelineData.iterations.push(timelineIteration);
    return timelineData;
  } catch (e) {
    return emptyIterationTimelineData;
  }
};

export const getIterationAccordingToType = iteration => {
  return iteration.type === 'weekly'
    ? {
        start_date: moment()
          .subtract(30, DAYS_PARAM)
          .format(SEARCH_DATE_FORMAT),
        end_date: moment().format(SEARCH_DATE_FORMAT),
        type: 'weekly'
      }
    : iteration;
};
